// ─── PRE-SURVEY READINESS MODEL ──────────────────────────────────────────────
//
// Ranks F-tags by how likely they are to be a focus of your next survey, and says
// WHY in sentences a DON or an administrator can act on.
//
// This lives in its own module rather than inside App.js because it is pure
// arithmetic over CMS data, and arithmetic that drives a clinical-facing ranking
// needs to be testable on its own. test/predictive-model.test.js drives it
// directly; before the split, none of it could be.
//
// ── WHAT WAS WRONG BEFORE, BECAUSE EACH ONE IS A RULE HERE NOW ──────────────
//
// 1. A FACILITY WITH NO DATA READ AS A FACILITY WITH NO RISK. Every component
//    scored 0 when its input was missing, the total fell under the cutoff, and
//    the screen said "No elevated risk areas identified based on current data"
//    over a green panel. Arithmetically: with no citations, no QM rating and no
//    staffing rating, the highest any tag could reach was exactly 10.0 against a
//    `> 10` filter — so the list was empty, and empty rendered as reassurance.
//    An absent input must never resolve to a plausible value. `inputs` now
//    reports what was actually available and `assessable` says whether the
//    ranking means anything at all.
//
// 2. 174 OF THE 200 F-TAGS IN THE LIBRARY COULD NEVER BE PREDICTED. The tag
//    universe was Object.keys(FTAG_DESCRIPTIONS) — 27 tags. Prior citation is
//    described as "the strongest single predictor" at 30% of the score, and a
//    citation at F656 (care planning), F604/F605 (restraints), F677, F867
//    (QAPI) or F882 contributed nothing and could not surface. The universe is
//    now the union of the described tags, the national-focus tags, and EVERY TAG
//    THIS FACILITY WAS ACTUALLY CITED AT.
//
// 3. POINTS WERE ADDED WITH NO REASON GIVEN. A QM rating of exactly 3, a
//    staffing rating of exactly 3, and a state rate between 5% and 15% each
//    added score and pushed no sentence. F676 with qm_rating 3 and
//    staffing_rating 3 scored 14.5 — above the cutoff, so it was displayed —
//    with ZERO bullets under the heading "Why this is flagged for you". Every
//    contribution now carries its own sentence: addScore() cannot be called
//    without one.
//
// 4. THE STATE COMPONENT WAS STRUCTURALLY DEAD. All five callers passed `[]` for
//    statePatterns, so 15% of the advertised model contributed nothing on every
//    run, while the brief told the reader state patterns were 15% of the
//    ranking. The score is now stated against the weight that was ACTUALLY
//    available, and the missing component is named.
//
// 5. THE SCORE HAD NO SCALE. "Score 19" against an implied 100 that the
//    arithmetic could exceed (45 + 15 + 14 + 15 + 12.5, then x1.2 for SFF =
//    121.8). Every result now carries `outOf` and `confidence`.
//
// 6. THE WORDING OVERSTATED WHAT IT KNEW. "Cited at F689 in your most recent
//    survey cycle" was printed on a <12-month test, which is not the same
//    statement. It now says when, from the date it actually has.
//
// ── WHAT THIS STILL CANNOT DO, SAID PLAINLY ────────────────────────────────
//
// The QM component reads the facility's QM STAR RATING, not its individual
// quality measures. FTAG_QM_MAPPINGS names the measures that drive each tag and
// they are used in the explanation, but the SCORE behind them is one star rating
// applied to every mapped tag alike. /api/cms/quality-measures/:ccn exists and
// would make this per-measure; until it is wired, inputs.qualityMeasures.basis
// says "star rating" so nobody reads more into it than is there.

// ─── F-tag to Quality Measure correlations ──────────────────────────────────
// Which QM signals elevate each tag. Based on CMS surveyor guidance and the
// LTCSP investigation pathways. Used in the EXPLANATION — naming the measures is
// what turns "your QM rating is low" into something a nurse can go and look at.
export const FTAG_QM_MAPPINGS = {
  F684: ["high-risk long-stay pressure ulcers", "long-stay falls with major injury", "antipsychotic use", "long-stay UTI"],
  F686: ["high-risk long-stay pressure ulcers", "low-risk long-stay pressure ulcers"],
  F689: ["long-stay falls", "long-stay falls with major injury", "short-stay falls"],
  F692: ["long-stay weight loss", "decline in ability to eat independently"],
  F676: ["long-stay ADL decline", "decline in ability to move independently"],
  // F758 WAS HERE. It is deleted from Appendix PP effective 28 April 2025 (QSO-25-14-NH)
  // and its guidance is cited under F605, chemical restraints — which is an ABUSE tag, so
  // the same finding now carries more weight than it did, not less.
  F605: ["antipsychotic use (long-stay)", "antipsychotic use (short-stay)"],
  F744: ["antipsychotic use", "behaviour symptoms affecting others"],
  F745: ["antipsychotic use", "depression symptoms"],
  F880: ["long-stay UTI", "infection rate"],
  F881: ["antibiotic use", "long-stay UTI"],
  F600: ["abuse icon on Care Compare"],
  F602: ["abuse icon on Care Compare"],
  F609: ["abuse icon on Care Compare"],
  F610: ["abuse icon on Care Compare"],
  F580: ["resident and family complaints"],
  F636: ["short-stay admissions"],
  F644: ["short-stay admissions", "PASRR completion"],
  F725: ["nurse hours per resident day below the state average", "staff turnover"],
  F726: ["nurse hours per resident day below the state average"],
  F837: ["administrator turnover", "DON turnover"],
  F838: ["administrator turnover"],
  F841: ["medical director turnover"],
};

// ─── F-tag clinical descriptions ────────────────────────────────────────────
// Concise descriptions derived from 42 CFR Part 483. These are the tags the model
// considers even with no citation history; a tag the facility HAS been cited at is
// added to the universe whether or not it appears here.
export const FTAG_DESCRIPTIONS = {
  F550: "Resident Rights / Dignity",
  F580: "Notify of Changes (Injury/Decline/Room/Etc.)",
  F600: "Free of Abuse and Neglect",
  F602: "Free from Misappropriation/Exploitation",
  // Chemical restraints. Since 28 April 2025 this is also where unnecessary PSYCHOTROPIC
  // medications are cited — the old F758 guidance was folded in here (QSO-25-14-NH).
  F604: "Right to be Free from Physical Restraints",
  F605: "Right to be Free from Chemical Restraints (incl. unnecessary psychotropics)",
  F609: "Reporting of Alleged Violations",
  F610: "Investigate/Prevent/Correct Alleged Violation",
  F636: "Comprehensive Assessments",
  F644: "Coordination of PASARR and Assessments",
  F676: "ADL Maintenance and Decline Prevention",
  F684: "Quality of Care",
  F686: "Treatment/Services to Prevent/Heal Pressure Ulcers",
  F689: "Free of Accident Hazards/Supervision/Devices",
  F692: "Nutrition / Hydration Status Maintenance",
  F725: "Sufficient Nursing Staff",
  F726: "Competent Nursing Staff",
  F744: "Treatment/Services for Dementia",
  F745: "Provide Medically Related Social Services",
  F803: "Menus and Nutritional Adequacy",
  F812: "Food Procurement/Store/Prepare/Serve - Sanitary",
  F837: "Governing Body",
  F838: "Facility Assessment",
  F841: "Responsibilities of Medical Director",
  F880: "Infection Prevention and Control",
  F881: "Antibiotic Stewardship",
  F908: "Essential Equipment, Safe Operating Condition",
  F921: "Safe/Functional/Sanitary/Comfortable Environment",
};

// ─── National enforcement focus areas (hardcoded — refresh quarterly) ───────
// Current CMS QSO memos, OIG workplans and state agency emphasis.
// Last reviewed: April 2026.
//
// NOTE ON WHAT THIS COMPONENT CAN AND CANNOT DO: it is the same for every
// facility in the country, so it cannot distinguish one facility from another. It
// is 10% of the model and it belongs there — it tells you where surveyors are
// being pointed nationally — but a tag that scores ONLY on this has told you
// nothing about your building, and `isLocalFinding` makes that visible.
export const NATIONAL_FOCUS_AREAS = {
  // The OIG review is real and current; the tag it is cited under is not F758 any more.
  // Carrying the highest boost in this table on a DELETED tag meant the brief regularly
  // named a requirement that no longer exists as a facility's top focus area.
  F605: { boost: 1.4, reason: "OIG 2025 review of antipsychotic use and schizophrenia diagnoses — now cited under F605, an abuse tag" },
  F880: { boost: 1.3, reason: "CMS February 2026 QSO memo on infection prevention" },
  F689: { boost: 1.2, reason: "Falls remain the top-cited tag nationally for long-stay residents" },
  F600: { boost: 1.3, reason: "OIG ongoing review of abuse-related incidents" },
  F684: { boost: 1.15, reason: "Persistent high-volume citation across all states" },
};

// ─── F-tag actionability ────────────────────────────────────────────────────
// HIGH: a pathway self-audit addresses it directly. MEDIUM: systems change over
// weeks. LOW: needs corporate, governing-body or HR action — a DON cannot close
// it alone, and saying so is more use than pretending they can.
export const ACTIONABILITY_SCORES = {
  F550: "high", F580: "high", F600: "high", F602: "medium",
  F609: "medium", F610: "medium", F636: "high", F644: "high",
  F676: "high", F684: "high", F686: "high", F689: "high",
  F692: "high", F725: "low", F726: "medium", F744: "high",
  F745: "high", F604: "high", F605: "high", F803: "high", F812: "high",
  F837: "low", F838: "low", F841: "low", F880: "high",
  F881: "high", F908: "medium", F921: "medium",
};

// ─── Weight constants (the heuristic model) ─────────────────────────────────
export const WEIGHTS = {
  PRIOR_CITATION: 30,
  QM_CORRELATION: 25,
  STAFFING: 20,
  STATE_PATTERN: 15,
  NATIONAL_FOCUS: 10,
};

// Tags whose citation tracks nurse coverage rather than a clinical process.
const STAFFING_SENSITIVE_TAGS = new Set([
  "F684", "F686", "F689", "F692", "F676", "F725", "F726", "F837", "F838", "F841",
]);

const MONTH = 1000 * 60 * 60 * 24 * 30;

/** "March 2025" from a date CMS gave us, or null if it gave us none. */
function monthYear(dateish) {
  if (!dateish) return null;
  const d = new Date(dateish);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

/** Scope/severity letters that mean actual harm or immediate jeopardy. */
const isHarmLevel = (code) => /^[G-L]$/.test(String(code || "").trim().toUpperCase());

/**
 * What to actually do, built from what drove the score.
 *
 * The old version had three sentences for every tag — "Run F689 pathway self-audit
 * this month", "Review systems for <description>, plan corrective actions over the
 * next 30 days", "Discuss <tag> risk in next QAPI / governance meeting". None told
 * the reader anything the ranking had not already said, and the middle one is not a
 * sentence anybody can act on.
 *
 * So the action is derived from the DRIVER. That keeps it specific without
 * inventing clinical guidance the model has no basis for: if a prior citation
 * raised the tag, the useful instruction concerns that citation's own plan of
 * correction; if the QM rating raised it, it concerns the residents behind those
 * measures.
 */
function recommendedActionFor({ tag, description, actionability, drivers, tagCitations }) {
  const parts = [];
  const prior = drivers.find((d) => d.component === "Prior citations");
  const qm = drivers.find((d) => d.component === "Quality measures");
  const staffing = drivers.find((d) => d.component === "Staffing");
  const state = drivers.find((d) => d.component === "State pattern");

  if (prior) {
    const when = monthYear(tagCitations?.[0]?.survey_date);
    parts.push(
      `Pull your plan of correction for the ${tag} citation${when ? ` from ${when}` : ""} and check that every corrective action in it is still being done. ` +
        "A repeat citation at a tag you have already corrected is what turns a finding into a pattern, and it is the most common way a survey escalates.",
    );
  }
  if (qm) {
    parts.push(
      `Pull the resident-level detail behind the quality measures listed above and work the outliers — the measure is what a surveyor arrives holding.`,
    );
  }
  if (staffing) {
    parts.push(
      `Check that your night and weekend coverage is documented and not merely worked: ${tag} is usually cited from the record rather than from observation.`,
    );
  }
  if (state && !prior && !qm) {
    parts.push(`Your state cites ${tag} often enough to be worth a walkthrough even with nothing else pointing at it.`);
  }

  // The next step, named by who can actually close it.
  if (actionability === "high") {
    parts.push(`Run the ${tag} pathway self-audit this month — it maps to the questions a surveyor will ask.`);
  } else if (actionability === "medium") {
    parts.push(
      `${tag} takes a systems change rather than a single audit, so put ${String(description).toLowerCase()} on this month's QAPI agenda with a named owner and a due date.`,
    );
  } else {
    parts.push(
      `${tag} cannot be closed at facility level — it needs the governing body, corporate or HR. Raise it with your administrator now rather than at the next survey.`,
    );
  }

  if (parts.length === 1) {
    // Only the actionability line, which means nothing local drove this.
    return `Nothing in your own CMS data raised ${tag} — it is on the national focus list. ${parts[0]}`;
  }
  return parts.join(" ");
}

/**
 * Which model components actually had data, and therefore how much of the
 * advertised 100 points was reachable at all.
 *
 * THIS IS THE FIX FOR THE WORST HOLE. Before, a missing input was
 * indistinguishable from a good one: both contributed 0, the total fell under the
 * cutoff, and an empty list rendered as a green "no elevated risk" reassurance.
 * Four states, not two — and "we could not assess this" is not "this is fine".
 */
export function describeInputs(facility, citations, statePatterns) {
  const hasCitations = Array.isArray(citations) && citations.length > 0;
  const hasQm = !!facility?.qm_rating;
  const hasStaffing = !!facility?.staffing_rating;
  const hasState = Array.isArray(statePatterns) && statePatterns.length > 0 && !!facility?.state;

  const parts = {
    priorCitations: {
      label: "Prior citation history",
      weight: WEIGHTS.PRIOR_CITATION,
      available: hasCitations,
      basis: hasCitations ? `${citations.length} citation${citations.length === 1 ? "" : "s"} from CMS` : null,
      missingMeans:
        "Without citation history the strongest signal in this model is absent. That is not the same as a clean record — " +
        "CMS may not have published your last survey yet, or this CCN may be new.",
      local: true,
    },
    qualityMeasures: {
      label: "Quality measure signals",
      weight: WEIGHTS.QM_CORRELATION,
      available: hasQm,
      // Named honestly: this is the star rating, not the individual measures.
      basis: hasQm ? `QM star rating ${facility.qm_rating} of 5 — the rating, not the individual measures` : null,
      missingMeans: "No QM rating was published for this facility, so the clinical component of the ranking is not in play.",
      local: true,
    },
    staffing: {
      label: "Staffing signals",
      weight: WEIGHTS.STAFFING,
      available: hasStaffing,
      basis: hasStaffing ? `Staffing star rating ${facility.staffing_rating} of 5` : null,
      missingMeans: "No staffing rating was published, so staffing-sensitive tags are not being raised by it.",
      local: true,
    },
    statePatterns: {
      label: "State surveyor patterns",
      weight: WEIGHTS.STATE_PATTERN,
      available: hasState,
      basis: hasState ? `${statePatterns.length} pattern rows for ${facility.state}` : null,
      // THIS COMPONENT HAS NEVER RUN IN THE APP. Every caller passed an empty
      // array while the brief told the reader it was 15% of the ranking.
      missingMeans:
        "State citation patterns are not loaded, so this 15% is not contributing. Rankings here rest on your own " +
        "facility's data and the national focus list only.",
      local: true,
    },
    nationalFocus: {
      label: "National enforcement focus",
      weight: WEIGHTS.NATIONAL_FOCUS,
      available: true,
      basis: `${Object.keys(NATIONAL_FOCUS_AREAS).length} tags on the current national focus list`,
      missingMeans: null,
      local: false,
    },
  };

  const all = Object.values(parts);
  const missing = all.filter((i) => !i.available);

  return {
    ...parts,
    availableWeight: all.filter((i) => i.available).reduce((s, i) => s + i.weight, 0),
    // The facility-specific components only. National focus is the same list
    // everywhere, so it cannot make a ranking mean something about YOUR building.
    localWeight: all.filter((i) => i.available && i.local).reduce((s, i) => s + i.weight, 0),
    available: all.filter((i) => i.available).map((i) => ({ label: i.label, weight: i.weight, basis: i.basis })),
    missing: missing.map((m) => m.label),
    missingDetail: missing.map((m) => ({ label: m.label, weight: m.weight, missingMeans: m.missingMeans })),
  };
}

/**
 * Score one F-tag for one facility.
 *
 * `opts.inputs` says which components have data — computed ONCE per brief so every
 * tag in a brief agrees about what was available.
 */
export function scoreFTagForFacility(tag, facility, citations, statePatterns, opts = {}) {
  const { tagLibrary = {}, inputs = null } = opts;
  const reasoning = [];
  const drivers = [];
  let score = 0;

  // ONE PLACE POINTS ARE ADDED, AND IT TAKES A SENTENCE.
  //
  // The fix for silent scoring. Three branches used to add points and push
  // nothing, so a card could rank a tag and leave "Why this is flagged for you"
  // blank. The reason is a required argument: the score cannot move without one.
  const addScore = (points, why, label) => {
    if (!(points > 0)) return;
    if (!why) throw new Error(`addScore for ${tag} was called with no reason — every point must be explainable`);
    score += points;
    reasoning.push(why);
    drivers.push({ component: label, points: Math.round(points * 10) / 10, why });
  };

  // ─── 1. Prior citation history (30%) ─────────────────────────────────────
  const tagCitations = (citations || [])
    .filter((c) => c.tag_short === tag)
    // SORTED HERE RATHER THAN TRUSTED. The old code took citations[0] as "the most
    // recent" on the strength of a comment saying the caller had sorted them. If
    // that ever stopped being true, "your most recent survey" named an arbitrary
    // row and nothing would have looked wrong.
    .slice()
    .sort((a, b) => new Date(b.survey_date || 0) - new Date(a.survey_date || 0));

  if (tagCitations.length > 0) {
    const mostRecent = tagCitations.find((c) => monthYear(c.survey_date)) || null;

    if (!mostRecent) {
      // A citation we cannot date cannot be aged, so it cannot be scored on
      // recency — but it is still a citation, and contributing nothing silently is
      // how "we were cited there" became invisible.
      reasoning.push(
        `CMS lists ${tagCitations.length === 1 ? "a citation" : `${tagCitations.length} citations`} at ${tag} for this facility with no survey date, ` +
          "so recency could not be weighted — check your own survey history before relying on this ranking.",
      );
    } else {
      const monthsSince = Math.floor((Date.now() - new Date(mostRecent.survey_date).getTime()) / MONTH);
      const when = monthYear(mostRecent.survey_date);
      let priorScore = 0;
      let why = null;

      if (monthsSince < 12) {
        priorScore = WEIGHTS.PRIOR_CITATION;
        why = `Cited at ${tag} in ${when} — inside the last 12 months, which is the strongest single signal in this model`;
      } else if (monthsSince < 24) {
        priorScore = WEIGHTS.PRIOR_CITATION * 0.7;
        why = `Cited at ${tag} in ${when} — one to two years ago`;
      } else if (monthsSince < 36) {
        priorScore = WEIGHTS.PRIOR_CITATION * 0.4;
        why = `Cited at ${tag} in ${when} — two to three years ago`;
      } else {
        // OVER THREE YEARS IS OLD NEWS, NOT NO NEWS, AND THE READER SHOULD SEE
        // WHICH. Scoring 0 silently made an old citation indistinguishable from
        // never having been cited at all.
        reasoning.push(
          `Cited at ${tag} in ${when}, more than three years ago — too old to raise this ranking, but it is on your record.`,
        );
      }

      if (priorScore > 0) {
        // Recurrence and harm are multipliers on the SAME component, so they join
        // one sentence rather than reading as three independent reasons.
        if (tagCitations.length >= 2) {
          priorScore *= 1.3;
          why += `. It appears ${tagCitations.length} times in the history CMS publishes, which is the recurrence pattern surveyors look for`;
        }
        const harm = tagCitations.find((c) => isHarmLevel(c.scope_severity_code));
        if (harm) {
          priorScore *= 1.4;
          why += `. One reached scope/severity ${String(harm.scope_severity_code).toUpperCase()} — actual harm or immediate jeopardy`;
        }
        addScore(Math.min(priorScore, WEIGHTS.PRIOR_CITATION * 1.5), why, "Prior citations");
      }
    }
  }

  // ─── 2. QM correlations (25%) ────────────────────────────────────────────
  //
  // The SCORE is the facility's QM star rating, not its individual measures. The
  // measures are named in the sentence because "your QM rating is 2/5" gives a DON
  // nothing to go and look at, while "the measures behind F689 are long-stay falls
  // and falls with major injury" tells them exactly where to start.
  const qmMeasures = FTAG_QM_MAPPINGS[tag];
  if (qmMeasures && facility.qm_rating) {
    const named = qmMeasures.slice(0, 3).join(", ");
    if (facility.qm_rating <= 2) {
      addScore(
        WEIGHTS.QM_CORRELATION * 0.6,
        `Your Quality Measure rating is ${facility.qm_rating} of 5 (below average). The measures that feed ${tag} are ${named} — those are the resident-level numbers to pull first`,
        "Quality measures",
      );
    } else if (facility.qm_rating === 3) {
      // One of the three branches that used to add points in silence.
      addScore(
        WEIGHTS.QM_CORRELATION * 0.3,
        `Your Quality Measure rating is 3 of 5 (average), which leaves ${tag} exposed rather than protected. Its measures are ${named}`,
        "Quality measures",
      );
    }
  }

  // ─── 3. Staffing (20%) ───────────────────────────────────────────────────
  if (STAFFING_SENSITIVE_TAGS.has(tag) && facility.staffing_rating) {
    if (facility.staffing_rating <= 2) {
      addScore(
        WEIGHTS.STAFFING * 0.7,
        `Your staffing rating is ${facility.staffing_rating} of 5 (below average) and ${tag} is staffing-sensitive — it is commonly cited where coverage gaps show in the record rather than where the care plan is wrong`,
        "Staffing",
      );
    } else if (facility.staffing_rating === 3) {
      addScore(
        WEIGHTS.STAFFING * 0.35,
        `Your staffing rating is 3 of 5 (average) and ${tag} is staffing-sensitive, so a bad month in coverage shows up here first`,
        "Staffing",
      );
    }
  }

  // ─── 4. State patterns (15%) ─────────────────────────────────────────────
  if (facility.state && Array.isArray(statePatterns) && statePatterns.length > 0) {
    const pattern = statePatterns.find((p) => p.state === facility.state && p.tag_short === tag);
    if (pattern && pattern.citation_rate > 0) {
      const pct = Math.round(pattern.citation_rate * 100);
      if (pattern.citation_rate > 0.30) {
        addScore(
          WEIGHTS.STATE_PATTERN,
          `${facility.state} surveyors cited ${tag} at ${pct}% of facilities in the last year — close to a standing item in this state`,
          "State pattern",
        );
      } else if (pattern.citation_rate > 0.15) {
        addScore(
          WEIGHTS.STATE_PATTERN * 0.6,
          `${tag} is moderately cited in ${facility.state} — ${pct}% of facilities`,
          "State pattern",
        );
      } else if (pattern.citation_rate > 0.05) {
        addScore(
          WEIGHTS.STATE_PATTERN * 0.3,
          `${tag} is cited at ${pct}% of ${facility.state} facilities — a low but non-zero state baseline`,
          "State pattern",
        );
      }
    }
  }

  // ─── 5. National focus (10%) ─────────────────────────────────────────────
  const nationalFocus = NATIONAL_FOCUS_AREAS[tag];
  if (nationalFocus) {
    addScore(
      (WEIGHTS.NATIONAL_FOCUS * (nationalFocus.boost - 1)) / 0.4,
      `${nationalFocus.reason} — this applies to every facility in the country, not only yours`,
      "National focus",
    );
  }

  // ─── SFF status amplifier ────────────────────────────────────────────────
  if (facility.special_focus_status && facility.special_focus_status !== "") {
    score *= 1.2;
    reasoning.unshift(
      `Your facility is ${facility.special_focus_status}, so every score on this page is raised by 20% — surveyors apply enhanced scrutiny across all tags`,
    );
  }

  // ── THE SCALE, STATED ────────────────────────────────────────────────────
  //
  // `outOf` is the weight actually AVAILABLE, not a notional 100. With no
  // state-pattern data the ceiling is 85, and 34 means something different against
  // 85 than against 100. A bare "Score 34" against an unstated maximum the
  // arithmetic could exceed is how this number became uninterpretable.
  const outOf = inputs ? inputs.availableWeight : 100;
  const rounded = Math.round(score * 10) / 10;
  const share = outOf > 0 ? rounded / outOf : 0;
  const actionability = ACTIONABILITY_SCORES[tag] || "medium";
  const description = FTAG_DESCRIPTIONS[tag] || tagLibrary[tag]?.title || tag;

  return {
    tag,
    description,
    score: rounded,
    outOf,
    confidence: Math.round(outOf),
    // A share of the AVAILABLE weight, so bands do not silently get harder to
    // reach as components drop out.
    band: share >= 0.40 ? "high" : share >= 0.20 ? "medium" : "low",
    actionability,
    reasoning,
    drivers,
    // ONLY the facility-specific components count as knowing something about this
    // building. A tag carried entirely by the national list has told the reader
    // nothing local, and the card must say so rather than look like a finding.
    isLocalFinding: drivers.some((d) => d.component !== "National focus"),
    // ── AND A FINER DISTINCTION THE RANKING CANNOT MAKE ON SCORE ALONE ──────
    //
    // "Prior citations" and "State pattern" are evidence about THIS TAG. The QM and
    // staffing star ratings are one number each for the whole facility, applied
    // identically to every tag in their tables — so the top of the ranking can be
    // decided by which tags happen to appear in both tables rather than by anything
    // observed about them. A 1-star QM plus a 1-star staffing rating scores 29 on
    // F689, F686, F676 and F692 alike, which outranks a citation at a tag from last
    // month (30 is close, and two proxies beat one signal).
    //
    // That is a defensible weighting — a facility at one star on both really is at
    // risk — so it is NOT reordered here. It is labelled, because "F689 is your top
    // focus area" reads as a finding about falls when the evidence is a facility-wide
    // rating, and the reader has to be able to tell those apart.
    evidenceIsTagSpecific: drivers.some((d) => d.component === "Prior citations" || d.component === "State pattern"),
    recommendedAction: recommendedActionFor({ tag, description, actionability, drivers, tagCitations }),
  };
}

/**
 * Build the Pre-Survey Readiness Brief.
 *
 * `assessable` is the field to read FIRST. When it is false the ranking carries no
 * facility-specific information, and the screen must say so rather than showing an
 * empty list over a green panel.
 */
export function generateReadinessBrief(facility, citations, statePatterns, opts = {}) {
  if (!facility) {
    return { error: "No CMS facility data available. Add this facility's CCN to enable predictions." };
  }
  const { tagLibrary = {}, nextRefreshLabel = null } = opts;
  const cites = Array.isArray(citations) ? citations : [];
  const patterns = Array.isArray(statePatterns) ? statePatterns : [];
  const inputs = describeInputs(facility, cites, patterns);

  // ── THE TAG UNIVERSE ─────────────────────────────────────────────────────
  //
  // Described tags + national focus + EVERY TAG THIS FACILITY WAS CITED AT. The
  // last of those is the fix: the universe used to be the 27 described tags, so
  // 174 of the 200 in the library — care planning, restraints, QAPI among them —
  // could not surface however recently they were cited, while the brief called
  // prior citations its strongest predictor.
  const universe = new Set([
    ...Object.keys(FTAG_DESCRIPTIONS),
    ...Object.keys(NATIONAL_FOCUS_AREAS),
    ...cites.map((c) => c.tag_short).filter(Boolean),
  ]);

  const scored = [...universe].map((tag) =>
    scoreFTagForFacility(tag, facility, cites, patterns, { tagLibrary, inputs }),
  );

  // The cutoff scales with the weight actually available, so losing a component
  // does not silently empty the list. A flat `> 10` against a reachable maximum of
  // exactly 10.0 is how a facility with no data got an empty brief.
  const cutoff = Math.max(4, inputs.availableWeight * 0.1);
  const meaningful = scored.filter((s) => s.score > cutoff);

  // ── THE ORDER ────────────────────────────────────────────────────────────
  //
  // The old comparator was `if (Math.abs(b.score - a.score) > 5) return b.score - a.score;
  // return actionRank[b] - actionRank[a]`, which is not a total order: with scores 30 (low
  // actionability), 27 (high) and 23 (high), 27 sorts above 30, 30 sorts above 23, and 27
  // ties with 23. A comparator that is not a weak ordering gives whatever the engine's sort
  // happens to produce, so the same data could rank differently in different browsers.
  //
  // It also ignored WHERE the score came from. A tag raised by an actual citation last
  // quarter could be pushed below a tag whose only evidence is "your QM star is 2", because
  // QM (25) plus staffing (20) outweighs prior citation (30) — while the model calls prior
  // citation its strongest single predictor. A finding about this building outranks a
  // finding about facilities like it.
  const actionRank = { high: 3, medium: 2, low: 1 };
  const citedRecently = (f) => (f.drivers || []).some((d) => d.component === "Prior citations");
  meaningful.sort((a, b) =>
    // Every term is a total order, and `tag` last makes the result deterministic.
    (b.score - a.score) ||
    (Number(citedRecently(b)) - Number(citedRecently(a))) ||
    (Number(b.isLocalFinding) - Number(a.isLocalFinding)) ||
    (actionRank[b.actionability] - actionRank[a.actionability]) ||
    a.tag.localeCompare(b.tag));

  const focusAreas = meaningful.slice(0, 3);

  // ── A TAG YOU WERE ACTUALLY CITED AT IS NEVER SILENTLY DROPPED ────────────
  //
  // The list was the top 3 plus the next 5, hard stop. A facility with six recent
  // citations had some of them fall off the end with nothing saying so — and an omission
  // is indistinguishable from "not a risk" to whoever is reading. Prior-citation tags
  // therefore keep their place, and anything still cut off is COUNTED.
  const rest = meaningful.slice(3);
  const watchList = rest.slice(0, 8);
  const omitted = rest.slice(8);
  focusAreas.forEach((f) => { f.focusLevel = f.band; });

  // ── IS THIS RANKING WORTH ANYTHING? ──────────────────────────────────────
  //
  // Only if something about THIS facility fed it. The national focus list is
  // identical for every building in the country.
  const assessable = inputs.localWeight > 0;
  const anyLocalFinding = focusAreas.some((f) => f.isLocalFinding);

  let verdict;
  if (!assessable) {
    verdict = {
      level: "cannot-assess",
      headline: "Not enough CMS data to rank your risk areas",
      detail:
        "None of the facility-specific inputs this model needs are available (" +
        inputs.missing.join(", ").toLowerCase() +
        "). Anything listed below comes from the national focus list, which is the same for every facility in the country. " +
        "Refresh CMS data, and check that this facility's CCN is correct.",
      whatToDo: "Fix the data before acting on this page: refresh CMS data, confirm the CCN, then come back.",
    };
  } else if (meaningful.length === 0) {
    verdict = {
      level: "assessed-clear",
      // SPECIFIC ABOUT WHAT WAS CHECKED. "No elevated risk areas identified" over
      // a green panel was the old message whether we had checked everything or
      // nothing at all.
      headline: "Nothing elevated in the data we hold",
      detail:
        `Checked against ${inputs.localWeight} of 100 points of facility-specific signal — ` +
        inputs.available.filter((a) => a.basis).map((a) => a.basis).join("; ") +
        ". That is a real result rather than an empty one, but it is only as complete as those inputs." +
        (inputs.missing.length ? ` Still missing: ${inputs.missing.join(", ").toLowerCase()}.` : ""),
      whatToDo:
        "Keep running pathway audits monthly. The value of a clear brief is knowing which areas you have NOT been told about — " +
        (inputs.missing.length ? `here that is ${inputs.missing.join(" and ").toLowerCase()}.` : "here, none."),
    };
  } else if (!anyLocalFinding) {
    verdict = {
      level: "national-only",
      headline: "Nothing in your own data is elevated — these are national focus areas",
      detail:
        "Every tag below is ranked by the national enforcement focus list, which is the same for every facility in the country. " +
        "Nothing specific to your building raised them.",
      whatToDo:
        "Treat these as general preparation rather than as findings about you. The specific work is whatever your own audits surface.",
    };
  } else {
    const top = focusAreas.filter((f) => f.isLocalFinding);
    verdict = {
      level: "ranked",
      headline: `${top.length} focus area${top.length === 1 ? "" : "s"} raised by your facility's own CMS data`,
      detail:
        `Ranked out of ${inputs.availableWeight} available points. ` +
        (inputs.missing.length
          ? `${inputs.missing.join(" and ")} ${inputs.missing.length === 1 ? "is" : "are"} not contributing — see what this brief could not see.`
          : "All five model components contributed."),
      whatToDo:
        `Start with ${top[0].tag}. ${top[0].recommendedAction}`,
    };
  }

  return {
    facility_name: facility.provider_name,
    facility_state: facility.state,
    facility_overall_rating: facility.overall_rating,
    sff_status: facility.special_focus_status,
    focus_areas: focusAreas,
    watch_list: watchList,
    // Counted rather than quietly cut. An omitted tag reads as "not a risk" to whoever is
    // looking at the list, which is the same mistake as an absent input reading as a good
    // one — so the number is reported and the screen can say "and N more".
    omitted_count: omitted.length,
    omitted_cited_count: omitted.filter((f) => (f.drivers || []).some((d) => d.component === "Prior citations")).length,
    generated_at: new Date().toISOString(),
    // The scale and the gaps travel WITH the brief, so no surface can render the
    // ranking without being able to render its limits.
    inputs,
    assessable,
    verdict,
    scoredTagCount: universe.size,
    methodology_summary:
      `Heuristic ranking over ${universe.size} F-tags using prior citation history (30%), quality-measure signals (25%), ` +
      `staffing signals (20%), state surveyor patterns (15%) and national enforcement focus (10%). ` +
      `On this brief, ${inputs.availableWeight} of those 100 points had data behind them.`,
    data_freshness: facility.fetched_at,
    next_refresh: nextRefreshLabel,
  };
}
